/**
 * API client. Every call sends credentials so the session cookie rides
 * along, and every non-2xx response is turned into a typed error the UI can
 * show directly rather than a generic "request failed."
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * The body of a successful response, or undefined when there is none.
 *
 * A 204 has no body by definition, and calling .json() on it throws
 * "Unexpected end of JSON input" -- after the request has already succeeded.
 * That shipped: revoking a grant and deleting a chat message both did their
 * job on the server and then reported an error, leaving the screen showing
 * the thing that had just been removed. Tests with curl never exercised this
 * path, which is why it got out. Read as text first, and parse only if
 * there is something to parse.
 */
export async function readBody<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text.length === 0 ? undefined : JSON.parse(text)) as T;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: "include",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(
      (body && typeof body === "object" && "error" in body ? String(body.error) : null) ??
        `Request failed (${response.status})`,
      response.status,
    );
  }

  return readBody<T>(response);
}

export type TenantRole = "owner" | "admin" | "viewer" | "member";

export interface Me {
  id: string;
  email: string;
  name: string | null;
  tenant: { id: string; name: string };
  role: TenantRole;
  /** An admin-issued password is still in place; the API allows nothing else. */
  mustChangePassword: boolean;
  canManageUsers: boolean;
  isPlatformAdmin: boolean;
}

export interface TenantUser {
  id: string;
  email: string;
  name: string | null;
  role: TenantRole;
  is_active: boolean;
  must_change_password: boolean;
  has_google: boolean;
  has_password: boolean;
  created_at: string;
  password_changed_at: string | null;
  last_seen_at: string | null;
  /** Access grants on parts of the tree, in this organization. */
  grant_count: number;
}

export type GrantPermission = "read" | "write" | "control" | "admin";

export interface UserGrant {
  id: string;
  group_id: string;
  group_name: string;
  permission: GrantPermission;
  created_at: string;
  granted_by_email: string | null;
}

export const grants = {
  list: (userId: string) =>
    request<{ role: TenantRole; grants: UserGrant[] }>(`/users/${userId}/grants`),
  add: (userId: string, groupId: string, permission: GrantPermission) =>
    request<{ id: string }>(`/users/${userId}/grants`, {
      method: "POST",
      body: JSON.stringify({ group_id: groupId, permission }),
    }),
  revoke: (userId: string, grantId: string) =>
    request<void>(`/users/${userId}/grants/${grantId}`, { method: "DELETE" }),
};

export const auth = {
  me: () => request<Me>("/auth/me"),
  login: (email: string, password: string) =>
    request<{ ok: true }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  config: () => request<{ googleSignInEnabled: boolean }>("/auth/config"),
};

export const users = {
  list: () => request<{ users: TenantUser[]; canManage: boolean }>("/users"),
  /**
   * The temporary password comes back exactly once, on creation. It is not
   * stored in plaintext and cannot be fetched again -- losing it means
   * issuing a new one.
   */
  /**
   * Creates an account, or adds an existing one to this organization. The
   * temporary password only comes back in the first case -- an existing
   * account keeps the password it already has.
   */
  create: (input: { email: string; name?: string | null; role: TenantRole }) =>
    request<{ id: string; temporary_password?: string; added_existing: boolean }>("/users", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  update: (id: string, input: { name?: string | null; role?: TenantRole; is_active?: boolean }) =>
    request<{ ok: true }>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  resetPassword: (id: string) =>
    request<{ temporary_password: string }>(`/users/${id}/reset-password`, { method: "POST" }),
  remove: (id: string) => request<{ ok: true }>(`/users/${id}`, { method: "DELETE" }),
};

export interface Organization {
  id: string;
  name: string;
  slug: string;
  role: TenantRole;
  device_count: string;
  member_count: string;
}

export const organizations = {
  list: () => request<{ organizations: Organization[]; activeId: string }>("/organizations"),
  create: (name: string, ownerEmail?: string | null) =>
    request<{ id: string; joined: boolean }>("/organizations", {
      method: "POST",
      body: JSON.stringify({ name, owner_email: ownerEmail ?? null }),
    }),
  rename: (id: string, name: string) =>
    request<{ ok: true }>(`/organizations/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  activate: (id: string) =>
    request<{ ok: true }>(`/organizations/${id}/activate`, { method: "POST" }),
};

export interface DeviceShare {
  id: string;
  tenant_id: string;
  tenant_name: string;
  permission: "read" | "control";
  alerts_enabled: boolean;
  created_at: string;
}

export const shares = {
  list: (deviceId: string) => request<{ shares: DeviceShare[] }>(`/devices/${deviceId}/shares`),
  targets: (deviceId: string) =>
    request<{ organizations: { id: string; name: string }[] }>(`/devices/${deviceId}/share-targets`),
  create: (deviceId: string, input: { tenant_id: string; permission: "read" | "control"; alerts_enabled: boolean }) =>
    request<{ id: string }>(`/devices/${deviceId}/shares`, { method: "POST", body: JSON.stringify(input) }),
  update: (deviceId: string, shareId: string, input: { permission?: "read" | "control"; alerts_enabled?: boolean }) =>
    request<{ ok: true }>(`/devices/${deviceId}/shares/${shareId}`, { method: "PATCH", body: JSON.stringify(input) }),
  remove: (deviceId: string, shareId: string) =>
    request<{ ok: true }>(`/devices/${deviceId}/shares/${shareId}`, { method: "DELETE" }),
};

export interface PlatformDevice {
  id: string;
  name: string;
  address: string;
  vendor: string;
  tenant_id: string;
  tenant_name: string;
  /** count() comes back from postgres.js as a string, to protect precision. */
  share_count: string;
}

export interface MoveResult {
  deviceName: string;
  fromTenantName: string;
  toTenantName: string;
  rowsMoved: Record<string, number>;
  rulesDeleted: number;
  eventsDeleted: number;
}

export interface AuditEntry {
  id: string;
  actor_email: string;
  action: string;
  subject_id: string | null;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface Connector {
  id: string;
  name: string;
  status: "pending" | "online" | "offline";
  enrolled_at: string | null;
  last_handshake_at: string | null;
  tunnel_address: string | null;
  public_key: string | null;
  lan_address: string | null;
  identity: string | null;
  routeros_version: string | null;
  enrollment_expires_at: string | null;
  awaiting_enrollment: boolean;
  device_count: string;
}

export interface ConnectorEnrollment {
  id: string;
  tunnel_address: string;
  expires_at: string;
  /** The two lines pasted into the router. Shown once; the token expires. */
  bootstrap: string;
}

export const connectors = {
  list: () => request<{ connectors: Connector[]; canManage: boolean }>("/connectors"),
  create: (name: string) =>
    request<ConnectorEnrollment>("/connectors", { method: "POST", body: JSON.stringify({ name }) }),
  remove: (id: string) => request<{ ok: true }>(`/connectors/${id}`, { method: "DELETE" }),
};

export interface PlatformOrg {
  id: string;
  name: string;
  slug: string;
  device_count: string;
  i_am_member?: boolean;
  has_owner?: boolean;
}

export interface PlatformMember {
  tenant_id: string;
  user_id: string;
  email: string;
  name: string | null;
  role: TenantRole;
  is_active: boolean;
  must_change_password: boolean;
  is_platform_admin: boolean;
  has_google: boolean;
  last_seen_at: string | null;
  org_count: string;
}

export interface UnassignedAccount {
  user_id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  is_platform_admin: boolean;
}

export const platform = {
  users: () =>
    request<{
      organizations: PlatformOrg[];
      members: PlatformMember[];
      unassigned: UnassignedAccount[];
    }>("/platform/users"),
  organizations: () =>
    request<{ organizations: { id: string; name: string; slug: string; device_count: string }[] }>(
      "/platform/organizations",
    ),
  devices: () => request<{ devices: PlatformDevice[] }>("/platform/devices"),
  moveDevice: (id: string, tenantId: string) =>
    request<MoveResult>(`/platform/devices/${id}/move`, {
      method: "POST",
      body: JSON.stringify({ tenant_id: tenantId }),
    }),
  audit: () => request<{ entries: AuditEntry[] }>("/platform/audit"),
  join: (id: string) =>
    request<{ ok: true }>(`/platform/organizations/${id}/join`, { method: "POST" }),
  leave: (id: string) =>
    request<{ ok: true }>(`/platform/organizations/${id}/join`, { method: "DELETE" }),
  tree: () =>
    request<{
      organizations: { id: string; name: string; slug: string }[];
      groups: Group[];
      devices: (DeviceSummary & { tenant_id: string })[];
    }>("/platform/tree"),
  deviceShares: (deviceId: string) =>
    request<{ shares: DeviceShare[] }>(`/platform/devices/${deviceId}/shares`),
  share: (deviceId: string, input: { tenant_id: string; permission: "read" | "control"; alerts_enabled: boolean }) =>
    request<{ id: string }>(`/platform/devices/${deviceId}/shares`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  unshare: (deviceId: string, shareId: string) =>
    request<{ ok: true }>(`/platform/devices/${deviceId}/shares/${shareId}`, { method: "DELETE" }),
};

export type AlertKind =
  | "device_stale"
  | "poll_failure"
  | "power_threshold"
  | "temperature_threshold"
  | "psu_failed";

export interface AlertRule {
  id: string;
  name: string;
  kind: AlertKind;
  severity: "warning" | "critical";
  enabled: boolean;
  scope_group_id: string | null;
  scope_device_id: string | null;
  group_name: string | null;
  device_name: string | null;
  metric: string | null;
  comparison: "above" | "below" | null;
  threshold: number | null;
  channel: number;
  stale_multiplier: number;
  trigger_after: number;
  resolve_after: number;
  renotify_minutes: number | null;
  open_count: string;
}

export interface AlertRuleInput {
  name: string;
  kind: AlertKind;
  severity: "warning" | "critical";
  enabled: boolean;
  scope_device_id?: string | null;
  scope_group_id?: string | null;
  metric?: string | null;
  comparison?: "above" | "below" | null;
  threshold?: number | null;
  trigger_after: number;
  resolve_after: number;
  renotify_minutes?: number | null;
  stale_multiplier?: number;
}

export interface AlertEvent {
  id: string;
  state: "open" | "resolved";
  opened_at: string;
  resolved_at: string | null;
  last_value: number | null;
  detail: string;
  notify_count: number;
  rule_name: string;
  kind: AlertKind;
  severity: "warning" | "critical";
  device_id: string;
  device_name: string;
}

export interface AlertMetric {
  id: string;
  label: string;
  unit: string;
  kind: AlertKind;
  typical: "above" | "below";
}

export const alerts = {
  rules: () => request<{ rules: AlertRule[]; canManage: boolean }>("/alerts/rules"),
  createRule: (input: AlertRuleInput) =>
    request<{ id: string }>("/alerts/rules", { method: "POST", body: JSON.stringify(input) }),
  updateRule: (id: string, input: AlertRuleInput) =>
    request<{ ok: true }>(`/alerts/rules/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteRule: (id: string) => request<{ ok: true }>(`/alerts/rules/${id}`, { method: "DELETE" }),
  events: () => request<{ events: AlertEvent[] }>("/alerts/events"),
  metrics: () => request<{ metrics: AlertMetric[] }>("/alerts/metrics"),
  preferences: () => request<{ preferences: { kind: AlertKind; email: boolean }[] }>("/alerts/preferences"),
  savePreferences: (preferences: { kind: AlertKind; email: boolean }[]) =>
    request<{ ok: true }>("/alerts/preferences", { method: "PUT", body: JSON.stringify({ preferences }) }),
};

export const account = {
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>("/account/password", {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),
};

export interface DeviceSummary {
  connector_id?: string | null;
  group_id?: string | null;
  is_shared?: boolean;
  owner_name?: string;
  /** Maintenance window: polled, alerting nobody. Until null = until ended. */
  maintenance_started_at?: string | null;
  maintenance_until?: string | null;
  id: string;
  name: string;
  address: string;
  vendor: string;
  protocol: string;
  enabled: boolean;
  poll_interval_seconds: number;
  last_poll_at: string | null;
  last_poll_status: "never" | "ok" | "partial" | "error";
  last_poll_error: string | null;
  last_poll_duration_ms: number | null;
  sys_name: string | null;
  board_name: string | null;
  routeros_version: string | null;
  serial_number: string | null;
  group_name: string | null;
  interface_count: string;
}

export interface DeviceInterface {
  id: string;
  if_index: number;
  if_name: string | null;
  if_alias: string | null;
  speed_bps: string | null;
  oper_status: number | null;
  admin_status: number | null;
  monitored: boolean;
}

export interface DeviceChannel {
  channel: number;
  label: string | null;
  enabled: boolean;
  collect_voltage: boolean;
  collect_current: boolean;
}

export interface DeviceRelay {
  relay_index: number;
  label: string | null;
  in_use: boolean;
}

export type Permission = "read" | "write" | "control" | "admin";

/** How a device is polled. Only returned to someone who may edit it. */
export interface DeviceSnmpConfig {
  snmp_version: "v2c" | "v3";
  snmp_port: number;
  snmp_v3_username: string | null;
  snmp_v3_auth_protocol: string | null;
  snmp_v3_priv_protocol: string | null;
  /** Whether each secret is stored. The secrets themselves are never returned. */
  has_community: boolean;
  has_v3_auth_key: boolean;
  has_v3_priv_key: boolean;
}

export interface DeviceDetail {
  snmp: DeviceSnmpConfig | null;
  device: DeviceSummary & {
    notes: string | null;
    sys_descr: string | null;
    group_id: string | null;
    /** PSU numbers watched; null means every supply that reports a state. */
    psu_monitored: (1 | 2)[] | null;
    /** Latest PSU states (0 ok, 1 failed), and when; all null without PSU sensors. */
    psu1_state: number | null;
    psu2_state: number | null;
    psu_at: string | null;
    maintenance_by_email: string | null;
  };
  interfaces: DeviceInterface[];
  channels: DeviceChannel[];
  relays: DeviceRelay[];
  /** What this viewer may do here -- the UI shows actions accordingly. */
  permissions: Permission[];
}

export interface Group {
  id: string;
  parent_id: string | null;
  name: string;
  kind: string | null;
  device_count?: string;
}

export const groups = {
  list: () => request<{ groups: Group[]; canManage: boolean }>("/groups"),
  create: (input: { name: string; kind?: string | null; parent_id: string }) =>
    request<{ id: string }>("/groups", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, input: { name?: string; kind?: string | null; parent_id?: string }) =>
    request<{ ok: true }>(`/groups/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  remove: (id: string) => request<{ ok: true }>(`/groups/${id}`, { method: "DELETE" }),
};

export const devices = {
  list: () => request<{ devices: DeviceSummary[] }>("/devices"),
  get: (id: string) => request<DeviceDetail>(`/devices/${id}`),
  /** Hours, or null for "until ended". */
  startMaintenance: (id: string, hours: number | null) =>
    request<{ ok: true; until: string | null }>(`/devices/${id}/maintenance`, {
      method: "POST",
      body: JSON.stringify({ hours }),
    }),
  endMaintenance: (id: string) => request<void>(`/devices/${id}/maintenance`, { method: "DELETE" }),
  create: (input: DeviceInput) =>
    request<{ id: string }>("/devices", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, input: Partial<DeviceInput>) =>
    request<{ ok: true }>(`/devices/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  remove: (id: string) => request<{ ok: true }>(`/devices/${id}`, { method: "DELETE" }),
};

/**
 * Fields accepted when creating or editing a device.
 *
 * Credentials are write-only: they are sent here but never come back from
 * the API. Omitting one on an edit leaves the stored secret alone; sending
 * null clears it.
 */
export interface DeviceInput {
  /** Null polls directly from the server, where the organisation may. */
  connector_id?: string | null;
  name: string;
  address: string;
  vendor: "mikrotik" | "generic" | "morningstar" | "tycon";
  protocol: "snmp" | "modbus_tcp";
  notes?: string | null;
  enabled?: boolean;
  poll_interval_seconds?: number;
  group_id?: string | null;
  psu_monitored?: (1 | 2)[] | null;
  snmp_port?: number;
  snmp_version?: "v2c" | "v3";
  snmp_community?: string | null;
  snmp_v3_username?: string | null;
  snmp_v3_auth_protocol?: string | null;
  snmp_v3_auth_key?: string | null;
  snmp_v3_priv_protocol?: string | null;
  snmp_v3_priv_key?: string | null;
  modbus_port?: number;
  modbus_unit_id?: number;
}

export type Resolution = "raw" | "5m" | "1h";

export interface SeriesResponse<T> {
  resolution: Resolution;
  bucketSeconds: number;
  points: T[];
}

export interface RangeParams {
  from: Date;
  to: Date;
  maxPoints?: number;
  channel?: number;
}

function rangeQuery(range: RangeParams): string {
  const params = new URLSearchParams({
    from: range.from.toISOString(),
    to: range.to.toISOString(),
  });
  if (range.maxPoints) params.set("maxPoints", String(range.maxPoints));
  if (range.channel !== undefined) params.set("channel", String(range.channel));
  return params.toString();
}

/**
 * Download URLs for the CSV exports.
 *
 * URLs rather than fetches: the browser's own download path handles the
 * Content-Disposition filename, the progress indicator and the save dialog,
 * none of which are worth rebuilding over a blob. Same origin, so the session
 * cookie goes along without anything being set here.
 */
export interface DeviceMessage {
  id: string;
  author_label: string;
  author_org_label: string;
  body: string;
  created_at: string;
  mine: boolean;
  can_delete: boolean;
}

export const messages = {
  list: (deviceId: string) =>
    request<{
      messages: DeviceMessage[];
      truncated: boolean;
      is_owner: boolean;
      /** Null for an organization the device is merely shared with. */
      shared_with_count: number | null;
      /** Newest message this reader has seen here, or null if never opened. */
      last_read_at: string | null;
    }>(`/devices/${deviceId}/messages`),
  markRead: (deviceId: string, through: string) =>
    request<void>(`/devices/${deviceId}/messages/read`, {
      method: "POST",
      body: JSON.stringify({ through }),
    }),
  unread: () =>
    request<{
      total: number;
      devices: { id: string; name: string; count: number; latest_at: string }[];
    }>("/messages/unread"),
  readAll: () => request<void>("/messages/read-all", { method: "POST" }),
  post: (deviceId: string, body: string) =>
    request<{ id: string; created_at: string }>(`/devices/${deviceId}/messages`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
  remove: (deviceId: string, messageId: string) =>
    request<void>(`/devices/${deviceId}/messages/${messageId}`, { method: "DELETE" }),
};

export const csvUrls = {
  interface: (id: string, range: RangeParams) =>
    `/api/metrics/interfaces/${id}/csv?${rangeQuery(range)}`,
  device: (id: string, range: RangeParams) =>
    `/api/metrics/devices/${id}/csv?${rangeQuery(range)}`,
  power: (id: string, range: RangeParams) =>
    `/api/metrics/power/${id}/csv?${rangeQuery(range)}`,
};

export interface InterfacePoint {
  t: string;
  in_bps: number | null;
  out_bps: number | null;
  in_bps_max: number | null;
  out_bps_max: number | null;
  in_errors: number | null;
  out_errors: number | null;
}

export interface DevicePoint {
  t: string;
  cpu_load: number | null;
  temperature_c: number | null;
  voltage_v: number | null;
  power_w: number | null;
}

export interface PowerPoint {
  t: string;
  voltage_v: number | null;
  current_a: number | null;
  power_w: number | null;
  temperature_c: number | null;
  temperature2_c: number | null;
  array_voltage_v: number | null;
  array_current_a: number | null;
  battery_temp_c: number | null;
  heatsink_temp_c: number | null;
  charge_state: number | null;
  kwh_total: number | null;
}

export interface RelayPoint {
  t: string;
  relay_index: number;
  duty_cycle: number;
}

export const metrics = {
  interface: (id: string, range: RangeParams) =>
    request<SeriesResponse<InterfacePoint>>(`/metrics/interfaces/${id}?${rangeQuery(range)}`),
  device: (id: string, range: RangeParams) =>
    request<SeriesResponse<DevicePoint>>(`/metrics/devices/${id}?${rangeQuery(range)}`),
  power: (id: string, range: RangeParams) =>
    request<SeriesResponse<PowerPoint>>(`/metrics/power/${id}?${rangeQuery(range)}`),
  relays: (id: string, range: RangeParams) =>
    request<SeriesResponse<RelayPoint>>(`/metrics/relays/${id}?${rangeQuery(range)}`),
  custom: (id: string, range: RangeParams) =>
    request<CustomSeriesResponse>(`/metrics/custom/${id}?${rangeQuery(range)}`),
};

export interface CustomSeriesResponse {
  resolution: Resolution;
  bucketSeconds: number;
  series: {
    /** Table row index, or "" for a scalar. */
    key: string;
    /** The row's label, when the metric has a label column. */
    label: string | null;
    points: { t: string; value: number | null; min: number | null; max: number | null }[];
  }[];
}

export interface CustomMetric {
  id: string;
  name: string;
  oid: string;
  is_table: boolean;
  label_oid: string | null;
  kind: "gauge" | "counter";
  scale: number;
  unit: string | null;
  mib_module: string | null;
  mib_object: string | null;
  enabled: boolean;
}

export interface CustomMetricInput {
  name: string;
  oid: string;
  is_table: boolean;
  label_oid?: string | null;
  kind: "gauge" | "counter";
  scale?: number;
  unit?: string | null;
}

export const customMetrics = {
  list: (deviceId: string) => request<{ metrics: CustomMetric[] }>(`/devices/${deviceId}/custom-metrics`),
  create: (deviceId: string, input: CustomMetricInput) =>
    request<{ id: string }>(`/devices/${deviceId}/custom-metrics`, { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, input: Partial<Pick<CustomMetric, "name" | "scale" | "unit" | "enabled" | "label_oid">>) =>
    request<{ ok: true }>(`/custom-metrics/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  remove: (id: string) => request<void>(`/custom-metrics/${id}`, { method: "DELETE" }),
};
